// PDF -> Bild-Daten fuer Barcode-/OCR-Erkennung.
//
// Verwendet die bereits eingebundene pdfjs-dist (Transitive von react-pdf).
// Wir rendern in mehreren Skalierungen, damit auch kleine QR-Codes im
// Briefkopf/Footer noch ausreichend Aufloesung haben.

import { pdfjs } from 'react-pdf';

export interface RenderedPage {
  pageNumber: number;
  scale: number;
  width: number;
  height: number;
  imageData: ImageData;
}

const DEFAULT_SCALES = [2.0, 3.0]; // ~144 / ~216 DPI bei A4

/** Liest ein PDF und rendert jede Seite in den angegebenen Skalierungen. */
export async function renderPdfPages(
  source: ArrayBuffer | Uint8Array,
  scales: number[] = DEFAULT_SCALES
): Promise<RenderedPage[]> {
  const data = source instanceof Uint8Array ? source : new Uint8Array(source);
  const loadingTask = pdfjs.getDocument({ data });
  const doc = await loadingTask.promise;
  const out: RenderedPage[] = [];
  try {
    for (let p = 1; p <= doc.numPages; p++) {
      const page = await doc.getPage(p);
      for (const scale of scales) {
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) continue;
        await page.render({ canvasContext: ctx, viewport }).promise;
        out.push({
          pageNumber: p,
          scale,
          width: canvas.width,
          height: canvas.height,
          imageData: ctx.getImageData(0, 0, canvas.width, canvas.height),
        });
        // Canvas explizit freigeben, sonst haelt Safari das Bitmap.
        canvas.width = 0;
        canvas.height = 0;
      }
      page.cleanup();
    }
  } finally {
    await doc.cleanup();
    await doc.destroy();
  }
  return out;
}

/** Liefert einen Crop einer ImageData als neue ImageData (ohne Canvas-Roundtrip). */
export function cropImageData(
  src: ImageData,
  x: number,
  y: number,
  w: number,
  h: number
): ImageData {
  const sx = Math.max(0, Math.floor(x));
  const sy = Math.max(0, Math.floor(y));
  const sw = Math.min(src.width - sx, Math.max(1, Math.floor(w)));
  const sh = Math.min(src.height - sy, Math.max(1, Math.floor(h)));
  const out = new ImageData(sw, sh);
  for (let row = 0; row < sh; row++) {
    const srcStart = ((sy + row) * src.width + sx) * 4;
    const dstStart = row * sw * 4;
    out.data.set(src.data.subarray(srcStart, srcStart + sw * 4), dstStart);
  }
  return out;
}

/** Extrahiert den Text-Layer (kein OCR — funktioniert nur fuer PDFs,
 *  die digital erzeugt sind, was bei Rechnungen >90% der Faelle ist). */
export async function extractPdfText(
  source: ArrayBuffer | Uint8Array
): Promise<string> {
  const data = source instanceof Uint8Array ? source : new Uint8Array(source);
  const loadingTask = pdfjs.getDocument({ data });
  const doc = await loadingTask.promise;
  const out: string[] = [];
  try {
    for (let p = 1; p <= doc.numPages; p++) {
      const page = await doc.getPage(p);
      const tc = await page.getTextContent();
      const items = tc.items as Array<{ str?: string }>;
      out.push(items.map(it => it.str ?? '').join(' '));
      page.cleanup();
    }
  } finally {
    await doc.cleanup();
    await doc.destroy();
  }
  return out.join('\n');
}

/** Liefert ImageData invertiert (manche QR-Codes sind weiss-auf-dunkel). */
export function invertImageData(src: ImageData): ImageData {
  const out = new ImageData(src.width, src.height);
  const s = src.data;
  const d = out.data;
  for (let i = 0; i < s.length; i += 4) {
    d[i] = 255 - s[i];
    d[i + 1] = 255 - s[i + 1];
    d[i + 2] = 255 - s[i + 2];
    d[i + 3] = s[i + 3];
  }
  return out;
}
